/**
 * Currency Conversion Utility
 * 
 * This utility handles currency conversion using exchange rates stored in the currency data.
 * Exchange rates are relative to a base currency: USD (US Dollars).
 * USD has exchangeRate = 1.0, all other currencies are relative to USD.
 */

interface CurrencyData {
    id: string;
    value: string;
    label: string;
    initial: string;
    exchangeRate?: number;
}

interface ConversionResult {
    originalAmount: number;
    originalCurrency: string;
    convertedAmount: number;
    targetCurrency: string;
    exchangeRate: number;
    timestamp: Date;
}

/**
 * Get exchange rate for a currency
 * @param currencyId - Currency ID to get exchange rate for
 * @param currencies - Array of available currencies
 * @returns Exchange rate or 1 if not found
 */
export const getExchangeRate = (
    currencyId: string,
    currencies: any[]
): number => {
    if (!currencyId || !currencies || currencies.length === 0) {
        return 1;
    }

    const currency = currencies.find((c: any) => c.value === currencyId || c.id === currencyId);
    return currency?.exchangeRate || 1;
};

/**
 * Get exchange rate by currency code
 * @param currencyCode - Currency code (e.g., "USD", "EUR")
 * @param currencies - Array of available currencies
 * @returns Exchange rate or 1 if not found
 */
export const getExchangeRateByCode = (
    currencyCode: string,
    currencies: any[]
): number => {
    if (!currencyCode || !currencies || currencies.length === 0) {
        return 1;
    }

    const currency = currencies.find((c: any) => 
        c.initial?.toUpperCase() === currencyCode.toUpperCase()
    );
    return currency?.exchangeRate || 1;
};

/**
 * Convert amount from one currency to another
 * @param amount - Amount to convert
 * @param fromCurrencyId - Source currency ID
 * @param toCurrencyId - Target currency ID
 * @param currencies - Array of available currencies
 * @returns Converted amount
 */
export const convertCurrency = (
    amount: number,
    fromCurrencyId: string,
    toCurrencyId: string,
    currencies: any[]
): ConversionResult => {
    // If same currency, return the same amount
    if (fromCurrencyId === toCurrencyId) {
        const currency = currencies.find((c: any) => c.value === fromCurrencyId);
        return {
            originalAmount: amount,
            originalCurrency: currency?.initial || "USD",
            convertedAmount: amount,
            targetCurrency: currency?.initial || "USD",
            exchangeRate: 1,
            timestamp: new Date()
        };
    }

    // Get exchange rates (relative to base currency: EUR)
    const fromRate = getExchangeRate(fromCurrencyId, currencies);
    const toRate = getExchangeRate(toCurrencyId, currencies);

    // Convert to base currency (EUR) first, then to target currency
    // Formula: amount / fromRate * toRate
    const convertedAmount = (amount / fromRate) * toRate;

    const fromCurrency = currencies.find((c: any) => c.value === fromCurrencyId);
    const toCurrency = currencies.find((c: any) => c.value === toCurrencyId);

    return {
        originalAmount: amount,
        originalCurrency: fromCurrency?.initial || "EUR",
        convertedAmount: parseFloat(convertedAmount.toFixed(2)),
        targetCurrency: toCurrency?.initial || "EUR",
        exchangeRate: toRate / fromRate,
        timestamp: new Date()
    };
};

/**
 * Convert amount by currency code
 * @param amount - Amount to convert
 * @param fromCurrencyCode - Source currency code (e.g., "USD")
 * @param toCurrencyCode - Target currency code (e.g., "EUR")
 * @param currencies - Array of available currencies
 * @returns Converted amount
 */
export const convertCurrencyByCode = (
    amount: number,
    fromCurrencyCode: string,
    toCurrencyCode: string,
    currencies: any[]
): ConversionResult => {
    // If same currency, return the same amount
    if (fromCurrencyCode.toUpperCase() === toCurrencyCode.toUpperCase()) {
        return {
            originalAmount: amount,
            originalCurrency: fromCurrencyCode.toUpperCase(),
            convertedAmount: amount,
            targetCurrency: toCurrencyCode.toUpperCase(),
            exchangeRate: 1,
            timestamp: new Date()
        };
    }

    // Get exchange rates (relative to base currency: EUR)
    const fromRate = getExchangeRateByCode(fromCurrencyCode, currencies);
    const toRate = getExchangeRateByCode(toCurrencyCode, currencies);

    // Convert to base currency (EUR) first, then to target currency
    const convertedAmount = (amount / fromRate) * toRate;

    return {
        originalAmount: amount,
        originalCurrency: fromCurrencyCode.toUpperCase(),
        convertedAmount: parseFloat(convertedAmount.toFixed(2)),
        targetCurrency: toCurrencyCode.toUpperCase(),
        exchangeRate: toRate / fromRate,
        timestamp: new Date()
    };
};

/**
 * Format currency amount with proper decimal places
 * @param amount - Amount to format
 * @param currencyId - Currency ID
 * @param currencies - Array of available currencies
 * @returns Formatted amount
 */
export const formatCurrencyAmount = (
    amount: number,
    currencyId: string,
    currencies: any[]
): string => {
    const currency = currencies.find((c: any) => c.value === currencyId);
    const decimalPlaces = currency?.SettingDecimalPlaces || 2;
    
    return amount.toFixed(decimalPlaces);
};

/**
 * Get currency information by ID
 * @param currencyId - Currency ID
 * @param currencies - Array of available currencies
 * @returns Currency object or null
 */
export const getCurrencyById = (
    currencyId: string,
    currencies: any[]
): any | null => {
    return currencies.find((c: any) => c.value === currencyId || c.id === currencyId) || null;
};

/**
 * Get currency information by code
 * @param currencyCode - Currency code (e.g., "USD")
 * @param currencies - Array of available currencies
 * @returns Currency object or null
 */
export const getCurrencyByCode = (
    currencyCode: string,
    currencies: any[]
): any | null => {
    return currencies.find((c: any) => 
        c.initial?.toUpperCase() === currencyCode.toUpperCase()
    ) || null;
};

/**
 * Convert and format amount with currency symbol
 * @param amount - Amount to convert
 * @param fromCurrencyId - Source currency ID
 * @param toCurrencyId - Target currency ID
 * @param currencies - Array of available currencies
 * @returns Formatted string with converted amount and symbol
 */
export const convertAndFormatCurrency = (
    amount: number,
    fromCurrencyId: string,
    toCurrencyId: string,
    currencies: any[]
): string => {
    const result = convertCurrency(amount, fromCurrencyId, toCurrencyId, currencies);
    const targetCurrency = getCurrencyById(toCurrencyId, currencies);
    const symbol = targetCurrency?.symbol || "$";
    
    return `${symbol}${formatCurrencyAmount(result.convertedAmount, toCurrencyId, currencies)}`;
};

/**
 * Batch convert multiple amounts at once
 * @param amounts - Array of amounts to convert
 * @param fromCurrencyId - Source currency ID
 * @param toCurrencyId - Target currency ID
 * @param currencies - Array of available currencies
 * @returns Array of conversion results
 */
export const batchConvertCurrency = (
    amounts: number[],
    fromCurrencyId: string,
    toCurrencyId: string,
    currencies: any[]
): ConversionResult[] => {
    return amounts.map(amount => 
        convertCurrency(amount, fromCurrencyId, toCurrencyId, currencies)
    );
};

/**
 * Calculate reverse conversion (from converted amount back to original)
 * @param convertedAmount - Converted amount
 * @param fromCurrencyId - Original currency ID
 * @param toCurrencyId - Current currency ID
 * @param currencies - Array of available currencies
 * @returns Original amount
 */
export const reverseConvertCurrency = (
    convertedAmount: number,
    fromCurrencyId: string,
    toCurrencyId: string,
    currencies: any[]
): number => {
    const result = convertCurrency(convertedAmount, toCurrencyId, fromCurrencyId, currencies);
    return result.convertedAmount;
};

/**
 * Check if currency conversion is needed
 * @param fromCurrencyId - Source currency ID
 * @param toCurrencyId - Target currency ID
 * @returns True if conversion is needed
 */
export const isConversionNeeded = (
    fromCurrencyId: string,
    toCurrencyId: string
): boolean => {
    return fromCurrencyId !== toCurrencyId;
};

/**
 * Get all supported currencies with their exchange rates
 * @param currencies - Array of available currencies
 * @returns Array of currencies with exchange rate info
 */
export const getSupportedCurrencies = (currencies: any[]): Array<{
    id: string;
    code: string;
    name: string;
    symbol: string;
    exchangeRate: number;
}> => {
    return currencies.map((c: any) => ({
        id: c.value || c.id,
        code: c.initial || "USD",
        name: c.label || c.name || "Unknown",
        symbol: c.symbol || "$",
        exchangeRate: c.exchangeRate || 1
    }));
};

export default {
    getExchangeRate,
    getExchangeRateByCode,
    convertCurrency,
    convertCurrencyByCode,
    formatCurrencyAmount,
    getCurrencyById,
    getCurrencyByCode,
    convertAndFormatCurrency,
    batchConvertCurrency,
    reverseConvertCurrency,
    isConversionNeeded,
    getSupportedCurrencies
};

